export interface CognitionTurnFailure {
  code?: string;
  message: string;
  retryable: boolean;
}

// Neutral wording on purpose: this failure surface serves SOLO brains as well
// as brain teams — "the team" made a solo brain's model failure read as hidden
// team machinery (a support ticket chased exactly that).
const FRIENDLY_FAILURE_MESSAGES: Record<string, string> = {
  bedrock_timeout: 'This answer took too long to generate. Please try again.',
  bedrock_error: 'This answer could not be completed. Please try again.',
  internal_error: 'Something went wrong while preparing this answer. Please try again.',
  // Deterministic content-size rejection (the board/attachments exceed the
  // model's reading window) — the backend sends retryable:false with it, so
  // no Retry button; the copy has to carry the "what now" instead.
  input_too_long:
    'There is more content here than can be read in one pass. Try a narrower question, a smaller time window, or splitting the largest blocks.',
};

/** Normalize the terminal SSE contract and the older persisted failed-row
 * shapes into one durable UI state. A plain history message is never treated
 * as error copy: only terminal events may use their generic `message` field. */
export function parseCognitionTurnFailure(value: unknown): CognitionTurnFailure | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const type = typeof raw.type === 'string' ? raw.type.toLowerCase() : '';
  const status = typeof raw.status === 'string' ? raw.status.toLowerCase() : '';
  const reason = typeof raw.reason === 'string' ? raw.reason : '';
  const messageType = typeof raw.messageType === 'string' ? raw.messageType.toUpperCase() : '';
  const persistedCognitionFailure =
    raw.cognitionSuccess === false &&
    (messageType === 'USER' ||
      status === 'failed' ||
      !!reason ||
      raw.error === true ||
      typeof raw.error_code === 'string');
  const failed =
    raw.error === true ||
    type === 'error' ||
    status === 'failed' ||
    status === 'error' ||
    reason === 'upstream_error' ||
    persistedCognitionFailure;
  if (!failed) return undefined;

  const errorCode =
    typeof raw.error_code === 'string' && raw.error_code
      ? raw.error_code
      : reason && reason !== 'upstream_error'
      ? reason
      : undefined;
  // Treat every terminal/provider message as untrusted diagnostic text. The
  // backend now emits bounded copy, but an older pod or a future unknown code
  // must not leak AWS hostnames, ARNs, stack traces, or SDK exceptions while
  // services roll independently.
  const errorMessage =
    (errorCode ? FRIENDLY_FAILURE_MESSAGES[errorCode] : undefined) ??
    'This answer could not be completed. Please try again.';

  return {
    ...(errorCode ? { code: errorCode } : {}),
    message: errorMessage,
    retryable: raw.retryable !== false,
  };
}
